import { transformEnumToDisplay } from '@fruitful/member-profile';
import { useAtomValue } from 'jotai/index';
import { Column } from 'primereact/column';
import {
    DataTable,
    type DataTableRowEditCompleteEvent,
} from 'primereact/datatable';
import { useState } from 'react';
import { H6Emphasized, Spinner, useToastController, YStack } from 'smoothie';

import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../common/components/Toasts';
import {
    adminUserAtom,
    selectedMembershipAtom,
} from '../../entities/entityAtoms';
import { type MemberDetails } from '../../entities/entityTypes';
import { InsuranceCoverageEditor } from '../../member/components/editors/InsuranceCoverageEditor';
import { useUpdateMemberAndRefetch } from '../../member/hooks/useUpdateMember';

interface MembershipInsuranceTableProps {
    members: MemberDetails[];
}

// A slimmed down version of the MemberInfo table component, just for Insurance.
export const MembershipInsuranceTable = ({
    members,
}: MembershipInsuranceTableProps) => {
    const toast = useToastController();

    const adminUser = useAtomValue(adminUserAtom);
    const canEditMember =
        adminUser.authorization.selectedMembership.general.canEdit;

    const membership = useAtomValue(selectedMembershipAtom);

    const [isSubmitting, setIsSubmitting] = useState(false);

    const { updateMemberAsync } = useUpdateMemberAndRefetch(membership);

    const onRowEditComplete = async ({
        newData,
    }: DataTableRowEditCompleteEvent) => {
        setIsSubmitting(true);
        const member: MemberDetails = newData as MemberDetails;

        try {
            const upsertMemberBody = {
                insuranceCoverage: member.insuranceCoverage,
            };
            await updateMemberAsync(member.id, upsertMemberBody);

            toast.show('Insurance updated!', MutationSuccessToastOptions);
        } catch (error) {
            toast.show('Error updating insurance', MutationErrorToastOptions());
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <YStack gap={12}>
            <H6Emphasized>Insurance details</H6Emphasized>
            {isSubmitting ? (
                <Spinner />
            ) : (
                <DataTable
                    value={members}
                    size="small"
                    tableStyle={{ width: '100%', fontSize: 13 }}
                    editMode="row"
                    onRowEditComplete={onRowEditComplete}
                    scrollable
                >
                    <Column field="id" header="id" hidden={true} />
                    <Column field="firstName" header="First Name" />
                    <Column field="lastName" header="Last Name" />
                    <Column
                        field="insuranceCoverage"
                        header="Insurance Coverage"
                        body={({ insuranceCoverage }: MemberDetails) => {
                            return insuranceCoverage != null
                                ? insuranceCoverage
                                      .map(transformEnumToDisplay)
                                      .join(', ')
                                : '';
                        }}
                        editor={(options) => InsuranceCoverageEditor(options)}
                        style={{
                            minWidth: 250,
                        }}
                    />
                    <Column rowEditor hidden={!canEditMember} />
                </DataTable>
            )}
        </YStack>
    );
};
